import type { CSSProperties, FC, RefObject } from "react";
import { useContext } from "react";
import { twMerge } from "tailwind-merge";

import { HoverableToolbarContext } from "components/hover-widgets/HoverableToolbarProvider";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import type { MenuChangeEvent, MenuDirection, MenuInstance } from "mds/components/Menu";
import { Menu, MenuItem } from "mds/components/Menu";
import {
  SquareIconButton,
  type SquareIconButtonProps,
} from "mds/components/buttons/SquareIconButton";
import { AddPlusIcon, OutcomeHashtagIcon } from "mds/icons";

export type OutcomeGroupMenu = {
  styles?: CSSProperties;
  size?: SquareIconButtonProps<"button">["size"];
  className?: string;
  buttonKind?: SquareIconButtonProps<"button">["kind"];
  direction?: MenuDirection;
  instanceRef?: RefObject<MenuInstance>;
  onAddOutcome: () => void;
  onAddOutcomeSubgroup: () => void;
};

export const OutcomeGroupMenu: FC<OutcomeGroupMenu> = ({
  styles,
  size = "s",
  className,
  buttonKind = "tertiary",
  direction = "right",
  instanceRef = null,
  onAddOutcome,
  onAddOutcomeSubgroup,
}) => {
  const MenuButton = (
    <SquareIconButton
      aria-label={t("common.add.outcome_or_outcome_subgroup")}
      className={twMerge("p-2", className)}
      kind={buttonKind}
      size={size}
    >
      <AddPlusIcon />
    </SquareIconButton>
  );

  const hoverableContext = useContext(HoverableToolbarContext);

  const onMenuChange = (event: MenuChangeEvent) => {
    if (hoverableContext) {
      hoverableContext.setShouldStayOpen(event.open);
    }
  };

  return (
    <Menu
      direction={direction}
      instanceRef={instanceRef}
      menuButton={MenuButton}
      menuClassName="min-w-[180px]"
      menuStyle={styles}
      onClick={(e) => e.stopPropagation()}
      onMenuChange={onMenuChange}
    >
      <MenuItem onClick={onAddOutcomeSubgroup}>
        <IconText iconStart={<AddPlusIcon />} text={t("common.add.outcome_subgroup")} />
      </MenuItem>
      <MenuItem onClick={onAddOutcome}>
        <IconText iconStart={<OutcomeHashtagIcon />} text={t("common.add.outcome")} />
      </MenuItem>
    </Menu>
  );
};
